import type { Candle } from "./types";

/** Colours are fixed by slot, so the same average is the same colour everywhere. */
export const MA_COLOURS = ["#2f7fd1", "#d98324", "#8a5fb0", "#3f9d6b", "#c0497a", "#6b7a8d"];

/**
 * The categorical palette, in a fixed order that is never cycled.
 *
 * Two selected sets rather than one inverted: the dark column is the same
 * eight hues stepped for a dark surface, not the light values lightened.
 */
export const SERIES_LIGHT = [
  "#2a78d6",
  "#eb6834",
  "#1baf7a",
  "#eda100",
  "#e87ba4",
  "#008300",
  "#6250d6",
  "#e34948",
];
export const SERIES_DARK = [
  "#3987e5",
  "#d95926",
  "#199e70",
  "#c98500",
  "#d55181",
  "#008300",
  "#9085e9",
  "#e66767",
];

/**
 * Daily candles folded into weekly ones.
 *
 * Weeks are keyed by their Monday, so a short first or last week still groups
 * correctly instead of being dropped. Open is the week's first open, close its
 * last close, and volume the sum — a week is one bar, not an average of five.
 */
export function toWeekly(candles: Candle[]): Candle[] {
  const weeks = new Map<string, Candle>();

  for (const c of candles) {
    const date = new Date(`${c.time}T00:00:00Z`);
    // getUTCDay(): 0 is Sunday, so shift it to the end of the week.
    const offset = (date.getUTCDay() + 6) % 7;
    date.setUTCDate(date.getUTCDate() - offset);
    const key = date.toISOString().slice(0, 10);

    const week = weeks.get(key);
    if (!week) {
      weeks.set(key, { ...c, time: key });
      continue;
    }
    week.high = Math.max(week.high, c.high);
    week.low = Math.min(week.low, c.low);
    week.close = c.close;
    week.volume = (week.volume ?? 0) + (c.volume ?? 0);
  }

  return [...weeks.values()];
}

/**
 * Moving average over closes, as {time, value} points.
 *
 * The first `period - 1` bars have no average rather than a partial one — a
 * 50-period line that starts as a 3-bar average would be read as signal.
 */
export function movingAverage(
  candles: Candle[],
  period: number,
  kind: "ema" | "sma",
): { time: string; value: number }[] {
  if (candles.length < period) return [];
  const out: { time: string; value: number }[] = [];

  if (kind === "sma") {
    let sum = 0;
    for (let i = 0; i < candles.length; i++) {
      sum += candles[i].close;
      if (i >= period) sum -= candles[i - period].close;
      if (i >= period - 1) out.push({ time: candles[i].time, value: sum / period });
    }
    return out;
  }

  // EMA seeds from the first `period` closes, which is the conventional start
  // and keeps it comparable with what a charting platform draws.
  const k = 2 / (period + 1);
  let ema = candles.slice(0, period).reduce((a, c) => a + c.close, 0) / period;
  out.push({ time: candles[period - 1].time, value: ema });
  for (let i = period; i < candles.length; i++) {
    ema = candles[i].close * k + ema * (1 - k);
    out.push({ time: candles[i].time, value: ema });
  }
  return out;
}


/**
 * A link that opens one symbol in TradingView.
 *
 * With a saved layout it opens *that* layout — your indicators, your drawings —
 * on this symbol. Without one it falls back to the plain chart URL, which works
 * when signed out but, for a signed-in user, TradingView redirects to whichever
 * layout was last open and drops the symbol on the way. That redirect is why a
 * bare link lands on the home page.
 */
export function tradingViewUrl(
  exchange: string,
  symbol: string,
  layout?: string | null,
): string {
  const ticker = encodeURIComponent(`${exchange}:${symbol}`);
  const base = layoutBase(layout) ?? "https://www.tradingview.com/chart";
  return `${base}/?symbol=${ticker}`;
}

/**
 * Normalises whatever was pasted into a chart base URL, or null.
 *
 * The regional domain is kept: a layout opened from in.tradingview.com belongs
 * to the same account but that is the host the person actually uses, and
 * rewriting it to www only invites another redirect. Only tradingview.com
 * hosts are accepted, so a stray value cannot be turned into a link out of the
 * app to somewhere else.
 */
export function layoutBase(value: string | null | undefined): string | null {
  const trimmed = (value ?? "").trim();
  if (!trimmed) return null;

  const fromUrl = trimmed.match(
    /^(?:https?:\/\/)?((?:[a-z0-9-]+\.)*tradingview\.com)\/chart\/([A-Za-z0-9]+)/i,
  );
  if (fromUrl) return `https://${fromUrl[1].toLowerCase()}/chart/${fromUrl[2]}`;

  // A bare layout id, which is what the id alone looks like.
  return /^[A-Za-z0-9]{4,40}$/.test(trimmed)
    ? `https://www.tradingview.com/chart/${trimmed}`
    : null;
}
